// src/auth.ts
var AuthManager = class {
  constructor(config = {}) {
    this._tokens = null;
    this._status = "unauthenticated";
    this._listeners = /* @__PURE__ */ new Set();
    this._refreshPromise = null;
    this._refreshTimer = null;
    this._config = config;
  }
  get status() {
    return this._status;
  }
  get isAuthenticated() {
    return this._status === "authenticated" || this._status === "refreshing";
  }
  get tokens() {
    return this._tokens;
  }
  get accessToken() {
    return this._tokens?.accessToken ?? null;
  }
  /** Subscribe to auth status changes */
  subscribe(fn) {
    this._listeners.add(fn);
    fn(this._status, this._tokens);
    return () => this._listeners.delete(fn);
  }
  /** Initialize from persisted storage */
  async initialize() {
    if (this._config.storage) {
      const saved = await this._config.storage.load();
      if (saved) {
        this._tokens = saved;
        if (this._isExpired(saved)) {
          await this._tryRefresh();
        } else {
          this._setStatus("authenticated");
          this._scheduleRefresh();
        }
      }
    }
  }
  /** Set tokens after login */
  async login(tokens) {
    this._tokens = tokens;
    this._setStatus("authenticated");
    this._scheduleRefresh();
    if (this._config.storage) {
      await this._config.storage.save(tokens);
    }
  }
  /** Clear tokens on logout */
  async logout() {
    this._tokens = null;
    this._clearRefreshTimer();
    this._refreshPromise = null;
    this._setStatus("unauthenticated");
    if (this._config.storage) {
      await this._config.storage.clear();
    }
  }
  /**
   * Get a valid access token, refreshing if needed.
   * Multiple concurrent callers share the same refresh promise.
   */
  async getValidToken() {
    if (!this._tokens) return null;
    if (this._isExpired(this._tokens)) {
      const refreshed = await this._tryRefresh();
      return refreshed?.accessToken ?? null;
    }
    return this._tokens.accessToken;
  }
  /**
   * Create a request interceptor for DryConnect.
   * Automatically attaches auth headers and handles token refresh.
   */
  createInterceptor() {
    return async (config) => {
      const token = this._config.getToken ? await this._config.getToken() : await this.getValidToken();
      if (token) {
        const headerName = this._config.headerName ?? "Authorization";
        const prefix = this._config.tokenPrefix ?? "Bearer";
        config.headers = {
          ...config.headers,
          [headerName]: `${prefix} ${token}`
        };
      }
      return config;
    };
  }
  /** Dispose all resources */
  dispose() {
    this._clearRefreshTimer();
    this._listeners.clear();
    this._refreshPromise = null;
  }
  // ─── Internal ──────────────────────────────────────
  _isExpired(tokens) {
    if (!tokens.expiresAt) return false;
    const buffer = this._config.refreshBuffer ?? 6e4;
    return Date.now() >= tokens.expiresAt - buffer;
  }
  async _tryRefresh() {
    if (!this._config.refreshToken || !this._tokens?.refreshToken) {
      this._setStatus("expired");
      this._config.onAuthExpired?.();
      return null;
    }
    if (this._refreshPromise) {
      return this._refreshPromise;
    }
    this._setStatus("refreshing");
    const refreshTimeout = new Promise(
      (_, reject) => setTimeout(() => reject(new Error("Token refresh timed out after 15s")), 15e3)
    );
    this._refreshPromise = Promise.race([
      this._config.refreshToken(this._tokens.refreshToken),
      refreshTimeout
    ]).then(async (newTokens) => {
      if (!newTokens || typeof newTokens.accessToken !== "string" || !newTokens.accessToken) {
        throw new Error("Token refresh returned invalid tokens: missing accessToken");
      }
      if (newTokens.expiresAt !== void 0 && typeof newTokens.expiresAt !== "number") {
        throw new Error("Token refresh returned invalid expiresAt: must be a number");
      }
      this._tokens = newTokens;
      this._setStatus("authenticated");
      this._scheduleRefresh();
      if (this._config.storage) {
        await this._config.storage.save(newTokens);
      }
      return newTokens;
    }).catch(() => {
      this._tokens = null;
      this._setStatus("expired");
      this._config.onAuthExpired?.();
      return null;
    }).finally(() => {
      this._refreshPromise = null;
    });
    return this._refreshPromise;
  }
  _scheduleRefresh() {
    this._clearRefreshTimer();
    if (!this._tokens?.expiresAt || !this._config.refreshToken) return;
    const buffer = this._config.refreshBuffer ?? 6e4;
    const delay = this._tokens.expiresAt - Date.now() - buffer;
    if (delay > 0) {
      this._refreshTimer = setTimeout(() => this._tryRefresh(), delay);
    } else {
      this._tryRefresh();
    }
  }
  _clearRefreshTimer() {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = null;
    }
  }
  _setStatus(status) {
    if (status === this._status) return;
    this._status = status;
    for (const fn of [...this._listeners]) {
      try {
        fn(status, this._tokens);
      } catch {
      }
    }
  }
};
function createAuthManager(config) {
  return new AuthManager(config);
}

// src/storage.ts
var MemoryTokenStorage = class {
  constructor() {
    this._tokens = null;
  }
  async save(tokens) {
    this._tokens = { ...tokens };
  }
  async load() {
    return this._tokens ? { ...this._tokens } : null;
  }
  async clear() {
    this._tokens = null;
  }
};
var LocalTokenStorage = class {
  constructor(key = "drystuff_auth") {
    this._key = key;
  }
  async save(tokens) {
    localStorage.setItem(this._key, JSON.stringify(tokens));
  }
  async load() {
    const data = localStorage.getItem(this._key);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  async clear() {
    localStorage.removeItem(this._key);
  }
};

// src/encrypted-storage.ts
var IV_LENGTH = 12;
var DEFAULT_KEY = "drystuff_auth_enc";
function getCrypto() {
  if (typeof globalThis.crypto?.subtle !== "undefined") return globalThis.crypto;
  throw new Error("Web Crypto API not available. Use a browser, service worker, or Node.js 19+.");
}
function uint8ToBase64(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}
function base64ToUint8(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function defaultPersistence() {
  if (typeof localStorage !== "undefined") {
    return {
      getItem: (k) => localStorage.getItem(k),
      setItem: (k, v) => localStorage.setItem(k, v),
      removeItem: (k) => localStorage.removeItem(k)
    };
  }
  throw new Error(
    "EncryptedTokenStorage: no localStorage available \u2014 pass a `persistence` adapter explicitly."
  );
}
var EncryptedTokenStorage = class {
  constructor(config) {
    this._getKey = config.getKey;
    this._persistence = config.persistence ?? defaultPersistence();
    this._key = config.storageKey ?? DEFAULT_KEY;
  }
  async save(tokens) {
    const c = getCrypto();
    const key = await this._getKey();
    const iv = c.getRandomValues(new Uint8Array(IV_LENGTH));
    const plaintext = new TextEncoder().encode(JSON.stringify(tokens));
    const ciphertext = await c.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
    const packed = new Uint8Array(IV_LENGTH + ciphertext.byteLength);
    packed.set(iv, 0);
    packed.set(new Uint8Array(ciphertext), IV_LENGTH);
    await this._persistence.setItem(this._key, uint8ToBase64(packed));
  }
  async load() {
    const stored = await this._persistence.getItem(this._key);
    if (!stored) return null;
    const packed = base64ToUint8(stored);
    const iv = packed.slice(0, IV_LENGTH);
    const ciphertext = packed.slice(IV_LENGTH);
    let plaintext;
    try {
      const key = await this._getKey();
      plaintext = await getCrypto().subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
    } catch (err) {
      const failure = new Error(
        "EncryptedTokenStorage: failed to decrypt tokens (wrong key, failed unlock, or corrupted data)"
      );
      failure.cause = err;
      throw failure;
    }
    return JSON.parse(new TextDecoder().decode(plaintext));
  }
  async clear() {
    await this._persistence.removeItem(this._key);
  }
};
function createEncryptedTokenStorage(config) {
  return new EncryptedTokenStorage(config);
}
async function deriveKeyFromPRF(prfOutput, options = {}) {
  const c = getCrypto();
  const material = await c.subtle.importKey(
    "raw",
    prfOutput,
    "HKDF",
    false,
    ["deriveKey"]
  );
  return c.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: options.salt ?? new Uint8Array(0),
      info: options.info ?? new TextEncoder().encode("drystuff-token-key")
    },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}
async function deriveKeyFromPassphrase(passphrase, salt, options = {}) {
  const c = getCrypto();
  const material = await c.subtle.importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return c.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: options.iterations ?? 6e5, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export { AuthManager, EncryptedTokenStorage, LocalTokenStorage, MemoryTokenStorage, createAuthManager, createEncryptedTokenStorage, deriveKeyFromPRF, deriveKeyFromPassphrase };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map